import { Component, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, combineLatest, filter, forkJoin, map, merge, of, switchMap, tap, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { ApiResponse, ApiService } from '../../../core/services/api.service';
import { StoreContextService } from '../../../core/services/store-context.service';
import { StoreRealtimeService } from '../../../core/services/store-realtime.service';
import { NetworkStatusService } from '../../../core/offline/network-status.service';
import { HubRoutingService } from '../../../core/offline/hub-routing.service';
import { OfflineSessionGuardService } from '../../../core/offline/offline-session-guard.service';
import { StorageKey, StorageService } from '../../../core/services/storage.service';
import { PosStateService } from '../pos-state.service';
import { CustomerDrawerComponent, CustomerSelection } from '../customer-drawer/customer-drawer.component';

interface Caller {
  id: string; phoneNumber: string; receivedAtUtc: string;
  customerId?: string; customerName?: string;
}
interface CallerInbox { enabled: boolean; calls: Caller[]; }
interface CallerCustomer { id: string; firstName: string; lastName: string; isActive: boolean; }

/**
 * CALLERID-RT — the inbox is read when the store channel says a call arrived
 * or was taken; this poll only covers a missed hint. AppSync delivers at most
 * once, and a call is listed for 2 minutes, so 30 s still shows it in time.
 */
const FALLBACK_POLL_MS = 30_000;
/** Fallback ticks to skip after the cloud says caller ID is off on this device (next read 5 min later). */
const DISABLED_RECHECK_TICKS = 9;
/** Fallback ticks to skip after a failed inbox read (next read 1 min later). */
const FAILED_RECHECK_TICKS = 1;
/** Store-channel events that mean "read the inbox now". They carry no number. */
const INBOX_HINTS = new Set(['caller-id.call-received', 'caller-id.call-taken', 'poll.refresh']);

@Component({
  selector: 'app-caller-id', standalone: true,
  imports: [CommonModule, TranslocoModule, CustomerDrawerComponent],
  template: `
    <aside *ngIf="visible && current && !selectedCall" class="caller-card" role="status" aria-live="polite">
      <span>{{ 'pos.callerId.incoming' | transloco }}
        <strong *ngIf="current.customerName">{{ current.customerName }}</strong>
        <strong *ngIf="!current.customerName" dir="ltr">{{ current.phoneNumber }}</strong>
        <span *ngIf="current.customerName" dir="ltr">{{ current.phoneNumber }}</span>
      </span>
      <button type="button" (click)="take(current)">{{ 'pos.callerId.takeDelivery' | transloco }}</button>
      <button type="button" (click)="dismiss(current)" [attr.aria-label]="'pos.callerId.dismiss' | transloco">×</button>
    </aside>
    <section *ngIf="selectedCall && visible">
      <p *ngIf="error" class="caller-error" role="alert">{{ error }}</p>
      <app-customer-drawer [showAddress]="true" [requireCustomer]="true"
        [initialMobile]="selectedCall.phoneNumber" [initialCustomerId]="selectedCall.customerId ?? null"
        initialOrderSource="Phone"
        (close)="cancel()" (selected)="createDelivery($event)" />
    </section>
  `,
  styles: [`
    /* Everything here is position:fixed; the host takes no layout space. */
    :host { display: contents; }
    .caller-card { position:fixed; inset-inline-end:20px; bottom:20px; z-index:1400;
      display:flex; align-items:center; flex-wrap:wrap; gap:12px; max-width:calc(100vw - 40px);
      padding:16px; border-radius:12px; background:var(--color-bg-surface); color:var(--color-text-primary);
      border:1px solid var(--color-border); box-shadow:var(--shadow-lg); }
    strong { display:block; font-size:20px; }
    button { padding:12px; border-radius:8px; cursor:pointer; border:1px solid var(--color-border);
      background:var(--color-bg-surface); color:var(--color-text-primary); }
    .caller-error { position:fixed; top:12px; inset-inline:20px; z-index:2000;
      padding:12px; background:var(--color-bg-surface); color:var(--color-text-primary); }
  `]
})
export class CallerIdComponent {
  private readonly api = inject(ApiService);
  private readonly net = inject(NetworkStatusService);
  readonly state = inject(PosStateService);
  private readonly store = inject(StoreContextService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hubRouting = inject(HubRoutingService);
  private readonly offlineSession = inject(OfflineSessionGuardService);
  private readonly storage = inject(StorageService);
  private readonly realtime = inject(StoreRealtimeService);
  private dismissed = new Set<string>();
  /**
   * Fallback ticks to skip before the next inbox read: caller ID is off here,
   * or the last read failed. Realtime hints are ignored while it is above 0.
   */
  private skipTicks = 0;
  /** Caller names resolved per call id; a call's number never changes, so one lookup is enough. */
  private names = new Map<string, Pick<Caller, 'customerId' | 'customerName'>>();
  calls: Caller[] = [];
  selectedCall: Caller | null = null;
  busy = false;
  error = '';

  get visible(): boolean {
    return this.net.current() === 'online' && !!this.state.posAuth && !this.state.isLocked
      && this.router.url.startsWith('/pos/');
  }
  /**
   * CALLERID-H1 — the inbox lives only in the cloud, so never ask while the
   * hub is serving this terminal (ConfirmBothWays keeps it there after the
   * internet returns) or while the POS token is hub-minted: the cloud cannot
   * verify that token, and its 401 used to sign the cashier out.
   */
  private get cloudReachable(): boolean {
    return !this.hubRouting.servedByHub()
      && !this.offlineSession.isOfflineMinted(this.storage.getRaw(StorageKey.POS_TOKEN));
  }
  get current(): Caller | undefined { return this.calls.find(c => !this.dismissed.has(c.id)); }

  constructor() {
    // Root-mounted: calls remain visible on the floor, order screen and settlement screen.
    // Switching store/user cancels the old request and clears all caller information.
    combineLatest([this.state.posAuth$, this.store.activeStore$]).pipe(
      tap(() => {
        this.calls = []; this.selectedCall = null; this.dismissed.clear(); this.error = '';
        this.skipTicks = 0; this.names.clear();
      }),
      switchMap(([auth, store]) => !auth || !store ? of(null) : merge(
        timer(0, FALLBACK_POLL_MS).pipe(map(() => 'tick' as const)),
        // A reconnect counts as a hint: AppSync does not replay what was
        // published while the socket was down.
        this.realtime.event$.pipe(filter(e => INBOX_HINTS.has(e.type)), map(() => 'hint' as const)),
        this.realtime.subscribed$.pipe(map(() => 'hint' as const)),
      ).pipe(
        map(trigger => {
          if (!this.visible || !this.cloudReachable) return 'clear' as const;
          // The device's showCallerId is known only from this reply. When it is
          // off, ask again every few minutes (an admin may turn it on) rather
          // than on every hint — most terminals never use caller ID.
          if (this.skipTicks > 0) {
            if (trigger === 'tick') this.skipTicks--;
            return 'skip' as const;
          }
          return 'read' as const;
        }),
        filter(step => step !== 'skip'),
        // A newer trigger restarts a read in flight, so a call committed
        // while the previous read was out is not missed.
        switchMap(step => step === 'clear' ? of(null) : this.api.get<CallerInbox>('pos/caller-id').pipe(
          tap(res => {
            this.skipTicks = !res.success ? FAILED_RECHECK_TICKS
              : res.data?.enabled ? 0 : DISABLED_RECHECK_TICKS;
            // Only pos-shell (pos/terminal) connects the store channel, and
            // the card also shows on cold-loaded order and settle screens.
            if (res.success && res.data?.enabled) this.realtime.ensureConnected(auth.tenantId, auth.storeId);
          }),
          switchMap(res => this.resolveCallerNames(res))))
      )),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(res => {
      this.calls = res?.success && res.data?.enabled ? res.data.calls : [];
      if (res?.success && !res.data?.enabled) this.selectedCall = null;
      const liveIds = new Set(this.calls.map(c => c.id));
      this.dismissed = new Set([...this.dismissed].filter(id => liveIds.has(id)));
      if (res) this.names = new Map([...this.names].filter(([id]) => liveIds.has(id)));
    });
  }

  private resolveCallerNames(res: ApiResponse<CallerInbox>): Observable<ApiResponse<CallerInbox>> {
    if (!res.success || !res.data?.enabled || !res.data.calls.length) return of(res);
    const inbox = res.data;
    // The CRM lookup applies the store's dial code to local numbers without
    // a fuzzy/suffix match or bypassing CRM permissions.
    // A call is listed for two minutes, so each one is looked up once, not on every read.
    // Failed lookups are not remembered and are tried again on the next read.
    return forkJoin(inbox.calls.map(call => {
      const known = this.names.get(call.id);
      if (known) return of({ ...call, ...known });
      return this.api.get<CallerCustomer | null>(`customers/by-mobile?mobile=${encodeURIComponent(call.phoneNumber)}`).pipe(
        map(customer => {
          if (!customer.success) return call;
          const match = customer.data;
          const name = match?.isActive ? { customerId: match.id,
            customerName: `${match.firstName} ${match.lastName}`.trim() } : {};
          this.names.set(call.id, name);
          return { ...call, ...name };
        })
      );
    })).pipe(map(calls => ({ ...res, data: { ...inbox, calls } })));
  }

  take(call: Caller): void { if (!this.busy) { this.selectedCall = call; this.error = ''; } }
  dismiss(call: Caller): void { this.dismissed.add(call.id); }
  cancel(): void { if (!this.busy) { this.selectedCall = null; this.error = ''; } }

  createDelivery(customer: CustomerSelection | null): void {
    if (!customer || !this.selectedCall || this.busy) return;
    const call = this.selectedCall;
    this.busy = true;
    this.error = '';
    this.api.post<{ id: string }>(`pos/caller-id/${call.id}/take-delivery`, {
      customerId: customer.customerId, deliveryAddressId: customer.addressId,
      channelType: 'Delivery', orderSource: 'Phone'
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      this.busy = false;
      if (res.success && res.data) {
        this.dismiss(call);
        this.selectedCall = null;
        this.router.navigate(['/pos/order', res.data.id]);
      } else {
        this.error = res.error || 'Unable to create delivery order.';
      }
    });
  }
}
